import { useAppStore } from "../shared/stores/useAppStore";
import { showContentLoading, hideContentLoading } from "../components/Layout";
import { UsersOrganization, UsersEmail, UsersSharedModel} from "../components/ModelItem/ModelListItemOptionMenu"
import { clearAllCookies, getCookie, setCookie } from "./Cookies";
import { ensureFreshAccessToken, refreshAccessToken, setAuthCookies } from "../shared/api/refreshAuth";

// Timing dates are normalized before persistence: start_date on the 1st of the
// month (matches the compute engine's day=1 iteration), end_date on the last
// day of the month (semantic: "end of period"). Mismatched days caused iloc
// out-of-bounds when start_date.day > end_date.day.
const normalizeTimingDateToFirstOfMonth = (dateStr: string | null | undefined): string | null => {
    if (!dateStr) return dateStr ?? null;
    // Expected shape: 'YYYY-MM-DD'
    return dateStr.slice(0, 8) + '01';
};

const normalizeTimingDateToEndOfMonth = (dateStr: string | null | undefined): string | null => {
    if (!dateStr) return dateStr ?? null;
    // Expected shape: 'YYYY-MM-DD'
    const [y, m] = dateStr.split('-').map(Number);
    // Day 0 of next month = last day of current month
    const lastDay = new Date(y, m, 0).getDate();
    return dateStr.slice(0, 8) + String(lastDay).padStart(2, '0');
};

class ApiService {
    host: string;
    access_token: string;
    token_type: string;
    // The cookie's access token as last adopted, to spot a rotation made elsewhere.
    cookie_access_token: string | null;
    default_header: object;
    set_error_snack_bar: Function;

    constructor(host: string) {
        this.host = host;
        this.access_token = '';
        this.token_type = '';
        this.cookie_access_token = getCookie("accessToken");

        if (getCookie("accessToken") && getCookie("tokenType"))
        {
            this.access_token = getCookie("accessToken");
            this.token_type = getCookie("tokenType");
        }
    }

    // Re-read access/token from cookies after a refresh so the next request
    // uses the rotated access token. Cookies are the source of truth.
    private syncTokensFromCookies() {
        this.access_token = getCookie("accessToken") || '';
        this.token_type = getCookie("tokenType") || '';
        this.cookie_access_token = getCookie("accessToken");
    }

    // The shared/api client and other tabs refresh into the cookies only. Adopt a
    // rotated cookie token instead of sending the old one: the API keeps one session
    // per user, so the old token is already revoked and its 401 would trigger a second
    // refresh that revokes everyone else's token in turn. Invitation and password-reset
    // flows set a token in memory without touching the cookie, so an unchanged cookie
    // never overrides it.
    private adoptRotatedCookieToken() {
        const cookieToken = getCookie("accessToken");
        if (cookieToken && cookieToken !== this.cookie_access_token) {
            this.cookie_access_token = cookieToken;
            this.access_token = cookieToken;
            this.token_type = getCookie("tokenType") || this.token_type;
        }
    }

    private async tryRefresh(): Promise<boolean> {
        const ok = await refreshAccessToken();
        if (ok) this.syncTokensFromCookies();
        return ok;
    }

    private async request_url_encoded(method: string, route: string, body: any) {
        const response = await fetch(this.host + route, {
            method: method,
            body: new URLSearchParams(body),
            headers: {
                'accept': 'application/json',
                'Content-Type': 'application/x-www-form-urlencoded',
            }
        })
        return response
    }

    private buildJsonHeaders(): HeadersInit {
        this.adoptRotatedCookieToken();
        return {
            'accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': this.token_type + ' ' + this.access_token,
        };
    }

    private async request_with_body_raw(method: string, route: string, body: any) {
        const doFetch = () => fetch(this.host + route, {
            method,
            body: JSON.stringify(body),
            headers: this.buildJsonHeaders(),
        }).catch(() => null);
        let response = await doFetch();
        if (response && response.status == 401) {
            const refreshed = await this.tryRefresh();
            if (!refreshed) {
                clearAllCookies();
                window.location.href = '/login';
                return null;
            }
            response = await doFetch();
            if (response && response.status == 401) {
                clearAllCookies();
                window.location.href = '/login';
                return null;
            }
        }
        return response
    }

    private async request_with_body(method: string, route: string, body: any, useBackDrop:boolean=true) {
        if (useBackDrop) {
            showContentLoading();
        }
        const doFetch = () => fetch(this.host + route, {
            method,
            body: JSON.stringify(body),
            headers: this.buildJsonHeaders(),
        }).catch(async(_e) => {
            useAppStore.getState().openErrorSnackBar();
            return null;
        })
        let response = await doFetch();
        if (response && response.status == 401) {
            const refreshed = await this.tryRefresh();
            if (refreshed) {
                response = await doFetch();
            }
        }
        if (useBackDrop) {
            hideContentLoading();
        }
        if (response && !response.ok) {
            if (response.status == 401) {
                clearAllCookies();
                window.location.href = '/login';
                return null;
            } else if (response.status == 403) {
                useAppStore.getState().openForbiddenSnackBar();
                return null;
            }
        }
        return response
    }

    private async request(method: string, route: string, useBackDrop:boolean=true) {
        if (useBackDrop) {
            showContentLoading();
        }
        const doFetch = () => fetch(this.host + route, {
            method,
            headers: this.buildJsonHeaders(),
        }).catch(async(_e) => {
            useAppStore.getState().openErrorSnackBar();
            return null;
        })
        let response = await doFetch();
        if (response && response.status == 401) {
            const refreshed = await this.tryRefresh();
            if (refreshed) {
                response = await doFetch();
            }
        }
        if (useBackDrop) {
            hideContentLoading();
        }
        if (response && !response.ok) {
            if (response.status == 401) {
                clearAllCookies();
                window.location.href = '/login';
                return null;
            } else if (response.status == 403) {
                useAppStore.getState().openForbiddenSnackBar();
                return null;
            }
        }
        return response
    }

    private async post(route: string, body: any, useBackDrop:boolean=true) {
        return await this.request_with_body('POST', route, body, useBackDrop)
    }

    private async get(route: string, useBackDrop:boolean=true) {
        return await this.request('GET', route, useBackDrop)
    }

    private async put(route: string, body: any, useBackDrop:boolean=true) {
        return await this.request_with_body('PUT', route, body, useBackDrop)
    }

    private async delete(route: string, useBackDrop:boolean=true) {
        return await this.request('DELETE', route, useBackDrop)
    }

    private async patch(route: string, body: any, useBackDrop:boolean=true) {
        return await this.request_with_body('PATCH', route, body, useBackDrop)
    }

    private async setAuth(response: any) {
        clearAllCookies();
        this.access_token = response.access_token
        this.token_type = response.token_type
        this.cookie_access_token = response.access_token
        setAuthCookies({
            access_token: response.access_token,
            token_type: response.token_type,
            refresh_token: response.refresh_token ?? null,
        });

        useAppStore.getState().triggerInit();
    }

    // Called on every (re)connect: the URL carries the token, so it must be the live one,
    // refreshed first if it is about to expire.
    async getWebSocketUrl(route: string) : Promise<string> {
        await ensureFreshAccessToken();
        this.adoptRotatedCookieToken();
        return this.host.replace('http', 'ws') + route + '?token=' + this.access_token
    }

    async classicLogin(email: string, password: string) {
        const response = await this.request_url_encoded('POST', '/token', {
            username: email,
            password: password,
        })
        if (response.ok) {
            const resp = await response.json()
            this.setAuth(resp);
            return resp
        }
        else {
            return null;
        }
    }

    async register(first_name: string, last_name: string, email: string, password: string) {
        const response = await this.request_with_body('POST', '/user', {
            first_name: first_name,
            last_name: last_name,
            email: email,
            password: password
        }, false)
        if (response) {
            if (response.status == 200) {
                this.setAuth(await response.json())
            }
        }
        return response
    }

    async getCurrentUser() {
        const response = await this.get('/user', false)
        return response
    }

    async sendConfirmationCode(confirmation_code: string) {
        const response = await this.post('/user/confirmation', {
            confirmation_code: confirmation_code
        }, false)
        return response
    }

    async sendEmailBackRequest() {
        const response = await this.post('/user/resend', {}, false)
        return response
    }

    async getExpirationTimeKey() {
        const response = await this.get('/user/expiration', false)
        return response
    }

    async checkValidInvitationBackOffice(access_token: string, token_type: string) {
        this.access_token = access_token;
        this.token_type = token_type;
        const response = await this.post('/user/invite/check', {}, false)
        return response
    }

    async registerFromInvitationBackOffice(first_name: string, last_name: string, email: string, password: string) {
        const response = await this.request_with_body('POST', '/user/invite/register', {
            first_name: first_name,
            last_name: last_name,
            email: email,
            password: password
        }, false)
        if (response) {
            this.setAuth(await response.json())
        }
        return response
    }

    ///////////////////////////////////////////////////////////////////////////////////

    async addUser(email: string) {
        const response = await this.request_with_body_raw('POST', '/organization/user', {
            email: email
        })
        return response
    }

    async registerFromInvitationOrganization(first_name: string, last_name: string, email: string, password: string) {
        const response = await this.request_with_body('POST', '/organization/user/register', {
            first_name: first_name,
            last_name: last_name,
            email: email,
            password: password
        }, false)
        if (response) {
            this.setAuth(await response.json())
        }
        return response
    }

    async checkValidInvitationOrganization(access_token: string, token_type: string) {
        this.access_token = access_token;
        this.token_type = token_type;
        const response = await this.post('/organization/user/check', {}, false)
        return response
    }
    
    async editRoleUser(id: number, role_slug: string) {
        const response = await this.put('/organization/user/' + id + "/role", {
            role_slug: role_slug,
        }, false)
        return response
    }

    async deleteUser(id: number) {
        const response = await this.delete('/organization/user/' + id, false)
        return response
    }

    async getOrganization() {
        const response = await this.get('/organization', false)
        return response
    }

    async createOrganization(
        name: string,
        address: string,
        city: string,
        post_code: string,
        country: string,
        billing_address: string,
        billing_city: string,
        billing_post_code: string,
        billing_country: string,
        ) {
        const response = await this.post('/organization', {
            name: name,
            address: address,
            city: city,
            post_code: post_code,
            country: country,
            billing_address: billing_address,
            billing_city: billing_city,
            billing_post_code: billing_post_code,
            billing_country: billing_country,
        }, false)
        return response
    }

    async editOrganization(
        name: string,
        address: string,
        city: string,
        post_code: string,
        country: string,
        billing_address: string,
        billing_city: string,
        billing_post_code: string,
        billing_country: string,
        ) {
        const response = await this.put('/organization', {
            name: name,
            address: address,
            city: city,
            post_code: post_code,
            country: country,
            billing_address: billing_address,
            billing_city: billing_city,
            billing_post_code: billing_post_code,
            billing_country: billing_country,
        }, false)
        return response
    }

    async getUsersOrganization() {
        const response = await this.get('/organization/user', false)
        return response
    }
    
    //////////////////////////////////////////////////////////////////////////////////

    async reportForgottenPassword(email: string) {
        const response = await this.post('/reset_password', {
            email: email,
        }, false)
        return response
    }

    async checkValidReset(access_token: string, token_type: string) {
        this.access_token = access_token;
        this.token_type = token_type;
        const response = await this.post('/reset_password/check', {}, false)
        return response
    }

    async resetPassword(password: string) {
        const response = await fetch(this.host + '/password', {
                method: 'PUT',
                body: JSON.stringify({password: password}),
                headers: {
                    'accept': 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': this.token_type + ' ' + this.access_token,
                }
            }
        )
        // const response = await this.put('/password', {
        //     password: password,
        // }, false)
        return response
    }

    async changePassword(current_password: string, password: string) {
        const response = await this.post('/change_password', {
            current_password: current_password,
            password: password,
        }, false)
        return response
    }

    //////////////////////////////////////////////////////////////////////////////////

    async checkValidInvitationSharedModel(access_token: string, token_type: string) {
        this.access_token = access_token;
        this.token_type = token_type;
        const response = await this.post('/shared_model/user/check', {}, false)
        return response
    }

    async getRootFolderSharedModels() {
        const response = await this.get('/folder_shared_models')
        return response
    }

    async createFolderSharedModels(name: string, folder_id: number) {
        const response = await this.post('/folder_shared_models', {
            name: name,
            folder_id: folder_id,
        })
        return response
    }

    async putFolderSharedModels(id: number, name: string, folder_id: number) {
        const response = await this.put('/folder_shared_models/' + id, {
            name: name,
            folder_id: folder_id,
        })
        return response
    }

    async deleteFolderSharedModels(id: number) {
        return await this.delete('/folder_shared_models/' + id)
    }

    async putModelSharedModels(id: number, folder_id: number) {
        const response = await this.put('/shared_model/' + id, {
            folder_id: folder_id,
        })
        return response
    }

    async leaveSharedModels(id: number) {
        const response = await this.post('/shared_model/' + id + "/leave", {})
        return response
    }

    async duplicateSharedModel(id: number) {
        return await this.post('/shared_model/' + id + '/duplicate', {});
    }

    async stopSharingModel(id: number) {
        return await this.delete('/shared_model/' + id)
    }

    async getUsersOrganizationSharedModel(id: number) {
        const response = await this.get('/share_model/' + id + '/users/organization', false)
        return response
    }

    async getUserRightsSharedModels(id: number) {
        const response = await this.get('/shared_model/' + id + "/user_rights", false)
        return response
    }

    async getUserSharer(id: number) {
        const response = await this.get('/shared_model/' + id + "/sharer")
        return response
    }

    async shareUsersOrganization(id: number, list: UsersOrganization[]) {
        return await this.post('/share_model/' + id, {
            users: list,
        });
    }

    async shareUsersEmail(id: number, list: UsersEmail[]) {
        return await this.post('/share_model/' + id + '/email', {
            users: list,
        });
    }

    async getUsersManageSharing(id: number) {
        const response = await this.get('/shared_model/' + id + '/users', false)
        return response
    }

    async manageUsersSharing(id: number, list: UsersSharedModel[]) {
        return await this.put('/shared_model/' + id + '/users', {
            users: list,
        });
    }

    async deleteUserSharing(id: number, user_id: number) {
        return await this.delete('/shared_model/' + id + '/user/' + user_id);
    }

    async registerFromSharedModel(first_name: string, last_name: string, email: string, password: string) {
        const response = await this.request_with_body('POST', '/shared_model/user/register', {
            first_name: first_name,
            last_name: last_name,
            email: email,
            password: password
        }, false)
        if (response) {
            this.setAuth(await response.json())
        }
        return response
    }

    //////////////////////////////////////////////////////////////////////////////////

    async getUserPreferences() {
        return await this.get('/user/preferences')
    }

    async putUserPreferences(
        dateSystem: string,
        roundPrecision: number,
        numberFormattingLocale: string,
        chartPrimaryColor: string | null = null,
    ) {
        return await this.put('/user/preferences', {
            'date_system': dateSystem,
            'round_precision': roundPrecision,
            'number_formatting_locale': numberFormattingLocale,
            'chart_primary_color': chartPrimaryColor,
        })
    }

    async getUserCommercialPlan() {
        return await this.get('/user/current_commercial_plan')
    }

    async getOrganizationCommercialPlans() {
        return await this.get("/organization/commercial_plans")
    }

    async getCommercialPlanLicencesDetails(commercialPlanId) {
        return await this.get("/commercial_plan/" + commercialPlanId + "/licences")
    }

    async getCommercialPlanRelatedModels(commercialPlanId) {
        return await this.get("/commercial_plan/" + commercialPlanId + "/models")
    }

    async assignLicenceToUser(commercialPlanId, userId) {
        return await this.post("/commercial_plan/" + commercialPlanId + "/licence/" + userId, {})
    }

    async unAssignLicenceFromUser(commercialPlanId, userId) {
        return await this.delete("/commercial_plan/" + commercialPlanId + "/licence/" + userId)
    }

    async getRootFolder() {
        const response = await this.get('/folder')
        return response
    }

    async createFolder(name: string, folder_id: number) {
        const response = await this.post('/folder', {
            name: name,
            folder_id: folder_id,
        })
        return response
    }

    async putFolder(id: number, name: string, folder_id: number) {
        const response = await this.put('/folder/' + id, {
            name: name,
            folder_id: folder_id,
        })
        return response
    }

    async deleteFolder(id: number) {
        return await this.delete('/folder/' + id)
    }

    async createModel(name: string, folder_id: number) {
        const response = await this.post('/model', {
            name: name,
            folder_id: folder_id,
        })
        return response
    }

    async getModel(modelId: number, useBackDrop: boolean = true) {
        const response = await this.get('/model/' + modelId, useBackDrop)
        return response
    }

    async putModel(id: number, name: string, folder_id: number) {
        const response = await this.put('/model/' + id, {
            name: name,
            folder_id: folder_id,
        })
        return response
    }

    async applyPreset(modelId: number, preset: string) {
        const response = await this.post('/model/' + modelId + '/apply-preset', {
            preset: preset,
        })
        return response
    }

    async duplicateModel(id: number) {
        return await this.post('/model/' + id + '/duplicate', {});
    }

    async deleteModel(id: number) {
        return await this.delete('/model/' + id)
    }

    async getModelDataByScenario(scenario_id: number, useBackDrop: boolean = true) {
        return this.get('/modelscenario/' + scenario_id + '/modeldata', useBackDrop)
    }

    async getScenarioSections(scenario_id: number, useBackDrop: boolean = true) {
        return this.get('/modelscenario/' + scenario_id + '/sections', useBackDrop)
    }

    async getSectionData(scenario_id: number, section_id: string, useBackDrop: boolean = true) {
        return this.get('/modelscenario/' + scenario_id + '/section/' + section_id + '/modeldata', useBackDrop)
    }

    async getModelDataByScenarioWithSensitivity(scenario_id: number, sensitivity_id: number, useBackDrop: boolean = true) {
        return this.get(`/modelscenario/${scenario_id}/sensitivity/${sensitivity_id}/modeldata`, useBackDrop)
    }

    async getOperationalModelDataByScenario(scenario_id: number) {
        return this.get('/modelscenario/' + scenario_id + '/modeldata/operational')
    }

    async getValuationModelDataByScenario(scenario_id: number) {
        return this.get('/operational/valuation_model_datas/' + scenario_id)
    }

    async computeValuationModelDataByScenario(scenario_id: number) {
        return this.post('/operational/valuation/' + scenario_id + '/compute', {})
    }

    async computeModelDataByScenario(scenario_id: number) {
        return this.get('/modelscenario/' + scenario_id + '/compute')
    }

    async launchComputeJobModelDataByScenario(scenario_id: number) {
        return this.post('/modelscenario/' + scenario_id + '/compute-job', {})
    }

    async getModelData(id: number) {
        return await this.get('/input-modeldata/' + id)
    }

    async getModelDataChildren(modeldata_id: number) {
        return await this.get('/input-modeldata/' + modeldata_id + '/children', false)
    }

    async getOutputModelDataChildren(modeldata_id: number) {
        return await this.get('/output-modeldata/' + modeldata_id + '/children', false)
    }

    async getScenarioOutputRoot(scenario_id: number) {
        return await this.get('/modelscenario/' + scenario_id + '/output-root', false)
    }

    async postModelData(name: string, type: string, scenario_id: number, parentId: number, position: number) {
        return await this.post('/input-modeldata', {
            name: name,
            type: type,
            scenario_id: scenario_id,
            parent_id: parentId,
            position,
        })
    }

    async duplicateModelData(sourceId: number, destId: number) {
        return await this.post('/input-modeldata/duplicate', {
            model_data_id: sourceId,
            model_data_dest_id: destId,
        })
    }

    async putModelData(id: number, name: string, position: number) {
        return await this.put(
            '/input-modeldata/' + id, {
                name: name,
                position: position
            }
        )
    }

    async putOutputModelData(id: number, position: number) {
        return await this.put(
            '/output-modeldata/' + id, {
                position: position
            }
        )
    }

    async deleteModelData(id: number) {
        return await this.delete('/input-modeldata/' + id)
    }

    async postModelScenario(name: string, modelId: number, modelScenarioSourceId: number | null) {
        return await this.post('/modelscenario', {
            name: name,
            model_id: modelId,
            model_scenario_source_id: modelScenarioSourceId,
        })
    }

    async putModelScenario(id: number, name: string) {
        return await this.put('/modelscenario/' + id, {
            name: name,
        })
    }

    async deleteModelScenario(id: number) {
        return await this.delete('/modelscenario/' + id)
    }

    async postSensitivity(name: string, scenarioId: number, sensitivitySourceId: number | null) {
        return await this.post('/sensitivities', {
            name: name,
            scenario_id: scenarioId,
            sensitivity_source_id: sensitivitySourceId,
        })
    }

    async getSensitivity(id: number) {
        return await this.get('/sensitivities/' + id)
    }

    async putSensitivity(id: number, name: string) {
        return await this.put('/sensitivities/' + id, {
            name: name,
        })
    }

    async deleteSensitivity(id: number) {
        return await this.delete('/sensitivities/' + id)
    }

    async upsertSensitivityValue(sensitivityModelDataId: number, name: string, type: string, value: any) {
        return await this.put(`/sensitivities/modeldata/${sensitivityModelDataId}/value`, {
            name: name,
            type: type,
            value: value,
        }, false)
    }

    async putSensitivityModelData(id: number, to_consider: boolean) {
        return await this.put('/sensitivities/modeldata/' + id, {
            to_consider: to_consider
        })
    }

    async postSensitivityTimingValue(time_serie_id: number, field_name: string) {
        return await this.post('/sensitivities/value/timing', {
            time_serie_id: time_serie_id,
            field_name: field_name
        }, false)
    }

    async putSensitivityTimingValue(id: number, start_date: string, end_date: string, periodicity: string) {
        return await this.put('/sensitivities/value/timing/' + id, {
            start_date: normalizeTimingDateToFirstOfMonth(start_date),
            end_date: normalizeTimingDateToEndOfMonth(end_date),
            periodicity: periodicity,
        }, false)
    }
    
    async putSensitivityValue(id: number, value: any) {
        return await this.put('/sensitivities/value/' + id, {
            value: value,
        }, false)
    }

    async putSensitivityTimeSerie(id: number, unit: string, timing_id: number | string) {
        return await this.put('/sensitivities/value/timeserie/' + id, {
            unit: unit,
            timing_id: timing_id,
        }, false)
    }

    async getSensitivityTimeSerie(id: number) {
        return await this.get('/sensitivities/value/timeserie/' + id)
    }

    async putSensitivityTimeSerieElem(id: number, date: string, value: number) {
        return await this.put('/sensitivities/value/timeserie/' + id + '/elem', {
            date: date,
            value: value
        }, false)
    }

    async putSensitivitiesTimeSerieElems(id: number, elems: any) {
        return await this.put('/sensitivities/value/timeserie/' + id + '/elems', elems, false)
    }

    async postSensitivityListElem(id: number, elem: any) {
        return await this.post('/sensitivities/value/list/' + id + '/elem', elem, false)
    }

    async putSensitivityListElem(id: number, elem: any) {
        return await this.put('/sensitivities/value/list/elem/' + id, elem, false)
    }

    async deleteSensitivityListElem(id: number) {
        return await this.delete('/sensitivities/value/list/elem/' + id, false)
    }

    async deleteSensitivityValue(id: number) {
        return await this.delete('/sensitivities/value/' + id, false)
    }

    async computeSensitivity(id: number) {
        return await this.get('/sensitivities/' + id + '/compute')
    }

    async launchComputeJobSensitivity(id: number) {
        return this.post('/sensitivities/' + id + '/compute-job', {})
    }

    async putModelDataValue(id: number, value: any) {
        return await this.put('/input-modeldata/value/' + id, {
            value: value
        }, false)
    }

    async getFieldValueCurrent(fieldValueId: number) {
        return this.get('/input-modeldata/value/' + fieldValueId + '/current', false)
    }

    async getRelationOptions(fieldId: number) {
        return await this.get('/input-modeldata/value/' + fieldId + '/relation-options', false)
    }

    async getTimingOptions(fieldId: number) {
        return await this.get('/input-modeldata/value/' + fieldId + '/timing-options', false)
    }

    async getListRelationOptions(fieldId: number, elemKey: string) {
        return await this.get('/input-modeldata/value/' + fieldId + '/list-relation-options?elem_key=' + elemKey, false)
    }

    async fillModelDataValueList(id: number, idList: any) {
        return await this.put('/input-modeldata/value/list/' + id + '/fill', {
            relation_id_list: idList
        }, true)
    }

    async clearModelDataValueList(id: number) {
        return await this.delete('/input-modeldata/value/list/' + id + '/clear', true)
    }

    async putModelDataTimingValue(id: number, start_date: string, end_date: string, periodicity: string) {
        return await this.put('/input-modeldata/value/timing/' + id, {
            start_date: normalizeTimingDateToFirstOfMonth(start_date),
            end_date: normalizeTimingDateToEndOfMonth(end_date),
            periodicity: periodicity,
        }, false)
    }

    async postModelDataTimingValue(time_serie_id: number, field_name: string) {
        return await this.post('/input-modeldata/value/timing', {
            time_serie_id: time_serie_id,
            field_name: field_name
        })
    }

    async deleteModelDataTimingValue(id: number) {
        return await this.delete('/input-modeldata/value/timing/' + id, false);
    }

    async putTimeSerie(id: number, unit: string, timing_id: number | string) {
        return await this.put('/input-modeldata/value/timeserie/' + id, {
            unit: unit,
            timing_id: timing_id,
        }, false)
    }

    async getTimeSerie(id: number) {
        return await this.get('/input-modeldata/value/timeserie/' + id)
    }

    async putTimeSerieElem(id: number, date: string, value: number) {
        return await this.put('/input-modeldata/value/timeserie/' + id + '/elem', {
            date: date,
            value: value
        }, false)
    }

    async putTimeSerieElems(id: number, elems: any) {
        return await this.put('/input-modeldata/value/timeserie/' + id + '/elems', elems, false)
    }

    async putOperationalOutputTimeSerieElem(id: number, date: string, value: number | null) {
        return await this.put('/operational/input-modeldata/value/timeserie/' + id + '/elem', {
            date: date,
            value: value
        }, false)
    }

    async putOperationalTimeSerieElems(id: number, elems: any) {
        return await this.put('/operational/input-modeldata/value/timeserie/' + id + '/elems', elems, false)
    }

    async postListElem(id: number, elem: any) {
        return await this.post('/input-modeldata/value/list/' + id + '/elem', elem, false)
    }

    async putListElem(id: number, elem: any) {
        return await this.put('/input-modeldata/value/list/elem/' + id, elem, false)
    }

    async deleteListElem(id: number) {
        return await this.delete('/input-modeldata/value/list/elem/' + id, false)
    }

    async getScenarioListHistory(modelId: number, useBackDrop: boolean = true) {
        return await this.get("/model/" + modelId + "/history/distinct_scenarios", useBackDrop)
    }

    async getScenarioHistory(modelId: number, scenarioId: number, useBackDrop: boolean = true) {
        return await this.get("/model/" + modelId + "/scenario/" + scenarioId + "/history", useBackDrop)
    }

    async getAuditDatas(scenarioId: number, useBackDrop: boolean = true) {
        return await this.get("/audit/" + scenarioId, useBackDrop)
    }

    async getSensitivityAuditDatas(sensitivityId: number, useBackDrop: boolean = true) {
        return await this.get("/audit/sensitivity/" + sensitivityId, useBackDrop)
    }

    async getReport(scenarioId: number, rounteName: string) {
        return await this.get("/model/export/" + scenarioId + "/" + rounteName)
    }

    async compareScenariosFields(scenarioId1: number, scenarioId2: number, fieldIds: number[]) {
        return await this.post("/modelscenario/compare_scenarios_fields", {
            scenario_id_1: scenarioId1,
            scenario_id_2: scenarioId2,
            field_ids: fieldIds,
        })
    }

    async compareScenariosFieldsFloatDetailed(scenarioId1: number, scenarioId2: number, fieldIds: number[], onProgress?: (progress: number, total: number) => void) {
        const submitResponse = await this.post("/modelscenario/compare_scenarios_fields_float_detailed", {
            scenario_id_1: scenarioId1,
            scenario_id_2: scenarioId2,
            field_ids: fieldIds,
        }, false)
        if (!submitResponse || submitResponse.status !== 202) return submitResponse;
        const { job_id } = await submitResponse.json();
        return await this.pollCompareJob(job_id, 150, 2000, onProgress);
    }

    async compareScenarioSensitivityFields(scenarioId: number, sensitivityId: number, fieldIds: number[]) {
        return await this.post("/modelscenario/compare_scenario_sensitivity_fields", {
            scenario_id: scenarioId,
            sensitivity_id: sensitivityId,
            field_ids: fieldIds,
        })
    }

    async compareScenarioSensitivityFieldsFloatDetailed(scenarioId: number, sensitivityId: number, fieldIds: number[], sensitivityActivated: boolean, onProgress?: (progress: number, total: number) => void) {
        const submitResponse = await this.post("/modelscenario/compare_scenario_sensitivity_fields_float_detailed", {
            scenario_id: scenarioId,
            sensitivity_id: sensitivityId,
            field_ids: fieldIds,
            sensitivity_activated: sensitivityActivated,
        }, false)
        if (!submitResponse || submitResponse.status !== 202) return submitResponse;
        const { job_id } = await submitResponse.json();
        return await this.pollCompareJob(job_id, 150, 2000, onProgress);
    }

    private async pollCompareJob(jobId: string, maxAttempts: number = 150, intervalMs: number = 2000, onProgress?: (progress: number, total: number) => void): Promise<Response | null> {
        for (let i = 0; i < maxAttempts; i++) {
            const response = await this.get(`/modelscenario/compare_job/${jobId}`, false);
            if (!response) return null;
            if (!response.ok) return response;

            const data = await response.json();
            if (data.status === 'pending') {
                if (onProgress && data.progress != null && data.total != null) {
                    onProgress(data.progress, data.total);
                }
                await new Promise(resolve => setTimeout(resolve, intervalMs));
                continue;
            }

            // Le résultat est prêt — recréer un Response pour que l'appelant puisse faire .json()
            return new Response(JSON.stringify(data), {
                status: 200,
                headers: { 'Content-Type': 'application/json' },
            });
        }
        // Timeout
        return new Response(JSON.stringify({ detail: 'Compare job timed out' }), {
            status: 504,
            headers: { 'Content-Type': 'application/json' },
        });
    }

    async downloadExcelWithFomulaes(scenarioId: number) {
        return await this.get("/worksheet/" + scenarioId + "/download-with-formulaes", false)
    }

    async downloadExcelSensitivityWithFomulaes(scenarioId: number, sensitivityId: number) {
        return await this.get("/worksheet/" + scenarioId + "/sensitivity/" + sensitivityId + "/download-with-formulaes", false)
    }

    async downloadExcelValuesOnly(scenarioId: number) {
        return await this.get("/worksheet/" + scenarioId + "/download", false)
    }

    async downloadExcelSensitivityValuesOnly(scenarioId: number, sensitivityId: number) {
        return await this.get("/worksheet/" + scenarioId + "/sensitivity/" + sensitivityId + "/download", false)
    }

    async submitWorksheetJob(scenarioId: number, sensitivityId?: number): Promise<Response | null> {
        const body: any = { scenario_id: scenarioId };
        if (sensitivityId) body.sensitivity_id = sensitivityId;
        return await this.post("/worksheet/generate-and-validate", body, false);
    }

    async pollWorksheetJob(
        jobId: string,
        onProgress?: (progress: number, total: number, stepLabel: string) => void
    ): Promise<any> {
        const maxAttempts = 180;
        const intervalMs = 2000;

        for (let i = 0; i < maxAttempts; i++) {
            const response = await this.get(`/worksheet/job/${jobId}`, false);
            if (!response) return null;

            if (!response.ok) {
                if (response.status === 500) {
                    const err = await response.json();
                    throw new Error(err.detail || "Job failed");
                }
                return null;
            }

            const data = await response.json();
            if (data.status === 'pending') {
                if (onProgress && data.progress != null && data.total != null) {
                    onProgress(data.progress, data.total, data.step_label || "");
                }
                await new Promise(resolve => setTimeout(resolve, intervalMs));
                continue;
            }

            // Completed — data contains the validation report
            return data;
        }
        throw new Error("Worksheet generation timed out");
    }

    async downloadWorksheetJobFile(jobId: string): Promise<Response | null> {
        return await this.get(`/worksheet/job/${jobId}/download`, false);
    }

    async getOperationalReport(scenarioId: number, rounteName: string, periods: string) {
        return await this.get("/model/export/" + scenarioId + "/" + rounteName + "/" + periods)
    }

    async getPresetList(modelId: number) {
        return await this.get('/presets/' + modelId)
    }

    async getSummary(scenarioId: number, useBackDrop: boolean = true) {
        return await this.get('/summary/' + scenarioId, useBackDrop)
    }

    async getSensitivitySummary(scenarioId: number, sensitivityId: number, useBackDrop: boolean = true) {
        return await this.get('/summary/' + scenarioId + '/' + sensitivityId, useBackDrop)
    }

    async getModelDataValueComments(modelDataValueId: number) {
        return await this.get("/input_model_data_value/" + modelDataValueId + "/comments")
    }

    async postComment(modelDataValueId: number, content: string) {
        return await this.post("/input_model_data_value_comments", {
            model_data_value_id: modelDataValueId,
            content: content,
        }) 
    }

    async updateComment(id: number, content: string) {
        return await this.put("/input_model_data_value_comments/" + id, {
            content: content,
        })
    }

    async deleteComment(id: number) {
        return await this.delete("/input_model_data_value_comments/" + id)
    }

    ///////////////////////////////////////////////////////////////////////////////////

    async getFormContact() {
        return await this.get('/contact')
    }

    async sendFormContact(topics: string[], modelId: string, description: string) {
        const response = await this.post('/contact', {
            topics: topics,
            modelId: modelId,
            description: description,
        }, false)
        return response
    }

    async disableTutorial() {
        return await this.post("/tutorial_advancement/disable", {})
    }

    async updateTutorialAdvancement(advancement: object) {
        return await this.put("/tutorial_advancement", advancement)
    }

    async enableMainTutorial() {
        return await this.post("/tutorial_advancement/enable", {})
    }

    ///////////////////////////////////////////////////////////////////////////////////

    async getErpConnections() {
        return await this.get('/erp_connections', true)
    }

    async getErpConnection(id: number) {
        return await this.get('/erp_connections/' + id, false)
    }

    async createErpConnection(type: string, erp_connection_config: object | null) {
        return await this.post('/erp_connections', {
            type: type,
            erp_connection_config: erp_connection_config,
        }, false)
    }

    // Type-specific connection endpoints
    async getSalesforceConnection(connectionId: number) {
        return await this.get('/salesforce_connections/' + connectionId, false)
    }

    async testSalesforceConnection(connectionId: number) {
        return await this.get('/salesforce_connections/' + connectionId + '/status', false)
    }

    async updateSalesforceConnection(
        connectionId: number,
        name: string,
        sf_instance_url: string,
        sf_client_id: string,
        sf_username: string,
        sf_domain: string,
        sf_private_key: string
    ) {
        return await this.put('/salesforce_connections/' + connectionId, {
            name,
            sf_instance_url,
            sf_client_id,
            sf_username,
            sf_domain,
            sf_private_key,
        }, false)
    }

    async createSalesforceConnection(
        organization_id: number,
        type: string,
        name: string,
        sf_instance_url: string,
        sf_client_id: string,
        sf_username: string,
        sf_domain: string,
        sf_private_key: string
    ) {
        return await this.post('/salesforce_connections', {
            organization_id,
            type,
            name,
            sf_instance_url,
            sf_client_id,
            sf_username,
            sf_domain,
            sf_private_key,
        }, false)
    }

    async deleteSalesforceConnection(connectionId: number) {
        return await this.delete('/salesforce_connections/' + connectionId, false)
    }

    async getSalesforceObjectTypes(connectionId: number) {
        return await this.get('/salesforce_connections/' + connectionId + '/objects_types', false)
    }

    async getSalesforceFields(connectionId: number, objectType: string) {
        return await this.get('/salesforce_connections/' + connectionId + '/fields/' + objectType, false)
    }

    async querySalesforceRecords(
        connectionId: number,
        objectType: string,
        filters: any[],
        fields: string[],
        batchSize: number,
        limit: number,
        cursor?: string
    ) {
        return await this.post('/salesforce_connections/' + connectionId + '/query', {
            object_type: objectType,
            filters: filters,
            fields: fields,
            batch_size: batchSize,
            limit: limit,
            cursor: cursor,
        }, false)
    }

    async createSalesforceBinding(
        sf_object: string,
        sf_record_id: string,
        sf_field: string,
        value_id: number,
        connection_id: number,
        scenario_id: number,
        key: string | null
    ) {
        return await this.post('/erp_connection_bindings/salesforce', {
            sf_object,
            sf_record_id,
            sf_field,
            value_id,
            connection_id,
            scenario_id,
            key,
        }, false)
    }

    async getErpBinding(modelDataValueId: number) {
        return await this.get('/input_model_data_value/' + modelDataValueId + '/erp_binding', false)
    }

    async getErpBindingWithKey(modelDataValueId: number, key: string) {
        return await this.get('/input_model_data_value/' + modelDataValueId + '/erp_binding/' + key, false)
    }

    async updateSalesforceBinding(
        bindingId: number,
        sf_object: string,
        sf_record_id: string,
        sf_field: string
    ) {
        return await this.patch('/erp_connection_bindings/salesforce/' + bindingId, {
            sf_object,
            sf_record_id,
            sf_field,
        }, false)
    }

    async deleteErpBinding(bindingId: number, key?: string | null) {
        const route = key
            ? '/erp_connection_bindings/' + bindingId + '/' + key
            : '/erp_connection_bindings/' + bindingId;
        return await this.delete(route, false)
    }

    async getSalesforceBindingValue(bindingId: number, key?: string | null) {
        const route = key
            ? '/erp_connection_bindings/salesforce/' + bindingId + '/value/' + key
            : '/erp_connection_bindings/salesforce/' + bindingId + '/value';
        return await this.get(route, false)
    }

    async getScenarioErpBindingsOverview(scenarioId: number) {
        return await this.get('/erp_connection_bindings/modelscenario/' + scenarioId, false)
    }

    async getFieldDependencies(scenarioId: number, fieldId: number) {
        return await this.get('/modelscenario/' + scenarioId + '/field/' + fieldId + '/dependencies', false)
    }

    async getDependencyGraph(scenarioId: number, modelDataId?: number) {
        const route = '/modelscenario/' + scenarioId + '/dependency-graph' + (modelDataId ? '?model_data_id=' + modelDataId : '');
        return await this.get(route, false)
    }

    // Library
    async getLibraryItems(parentModelDataId?: number) {
        const query = parentModelDataId ? '?parent_model_data_id=' + parentModelDataId : '';
        return await this.get('/library' + query, false)
    }

    async getLibraryItem(itemId: number) {
        return await this.get('/library/' + itemId, false)
    }

    async getLibraryWorkspace() {
        return await this.get('/library/workspace', false)
    }

    async getLibraryCreatableTypes() {
        return await this.get('/library/creatable-types', false)
    }

    async createEmptyLibraryItem(type: string, name?: string, description?: string | null) {
        return await this.post('/library', {
            type,
            name: name || null,
            description: description || null,
        })
    }

    async saveToLibrary(name: string, description: string | null, sourceModelDataId: number, includeChildren: boolean) {
        return await this.post('/library/save', {
            name,
            description,
            source_model_data_id: sourceModelDataId,
            include_children: includeChildren,
        })
    }

    async previewSaveToLibrary(sourceModelDataId: number, includeChildren: boolean) {
        return await this.post('/library/save/preview', {
            source_model_data_id: sourceModelDataId,
            include_children: includeChildren,
        })
    }

    async updateLibraryItem(itemId: number, name?: string, description?: string | null) {
        return await this.put('/library/' + itemId, { name, description }, false)
    }

    async deleteLibraryItem(itemId: number) {
        return await this.delete('/library/' + itemId)
    }

    async applyLibraryItem(libraryItemId: number, destModelDataId: number, scenarioId: number) {
        return await this.post('/library/apply', {
            library_item_id: libraryItemId,
            dest_model_data_id: destModelDataId,
            scenario_id: scenarioId,
        })
    }

    async getGlobalLibraryBlocks(parentModelDataId?: number) {
        const query = parentModelDataId ? '?parent_model_data_id=' + parentModelDataId : '';
        return await this.get('/library/global' + query, false)
    }

    async getGlobalLibraryBlock(slug: string) {
        return await this.get('/library/global/' + encodeURIComponent(slug), false)
    }

    async saveGlobalLibraryBlock(name: string, description: string | null, sourceModelDataId: number, includeChildren: boolean) {
        return await this.post('/library/global', {
            name,
            description,
            source_model_data_id: sourceModelDataId,
            include_children: includeChildren,
        })
    }

    async deleteGlobalLibraryBlock(slug: string) {
        return await this.delete('/library/global/' + encodeURIComponent(slug))
    }

    async applyGlobalLibraryBlock(slug: string, destModelDataId: number, scenarioId: number) {
        return await this.post('/library/global/apply', {
            slug,
            dest_model_data_id: destModelDataId,
            scenario_id: scenarioId,
        })
    }
    // ──────────────────────────────────────────────
    // Portfolio Groups
    // ──────────────────────────────────────────────

    async getPortfolioGroups() {
        return await this.get('/portfolio_groups', false)
    }

    async createPortfolioGroup(data: any) {
        return await this.post('/portfolio_groups', data)
    }

    async getPortfolioGroup(groupId: number) {
        return await this.get('/portfolio_groups/' + groupId, false)
    }

    async updatePortfolioGroup(groupId: number, data: any) {
        return await this.put('/portfolio_groups/' + groupId, data)
    }

    async deletePortfolioGroup(groupId: number) {
        return await this.delete('/portfolio_groups/' + groupId)
    }

    // Portfolio Assets
    async addPortfolioAsset(groupId: number, data: any) {
        return await this.post('/portfolio_groups/' + groupId + '/assets', data)
    }

    async updatePortfolioAsset(groupId: number, assetId: number, data: any) {
        return await this.put('/portfolio_groups/' + groupId + '/assets/' + assetId, data)
    }

    async removePortfolioAsset(groupId: number, assetId: number) {
        return await this.delete('/portfolio_groups/' + groupId + '/assets/' + assetId)
    }

    // Portfolio Tags
    async createPortfolioTag(groupId: number, data: any) {
        return await this.post('/portfolio_groups/' + groupId + '/tags', data)
    }

    async deletePortfolioTag(groupId: number, tagId: number) {
        return await this.delete('/portfolio_groups/' + groupId + '/tags/' + tagId)
    }

    // Portfolio Sharing
    async sharePortfolioGroup(groupId: number, data: any) {
        return await this.post('/portfolio_groups/' + groupId + '/sharing', data)
    }

    async getPortfolioGroupSharing(groupId: number) {
        return await this.get('/portfolio_groups/' + groupId + '/sharing', false)
    }

    async deletePortfolioGroupSharing(groupId: number, sharingId: number) {
        return await this.delete('/portfolio_groups/' + groupId + '/sharing/' + sharingId)
    }

    async getPortfolioGroupAvailableUsers(groupId: number) {
        return await this.get('/portfolio_groups/' + groupId + '/sharing/available_users', false)
    }

    // Portfolio Compute & Views
    async computePortfolio(groupId: number) {
        return await this.post('/portfolio_groups/' + groupId + '/compute', {})
    }

    async getPortfolioOverview(groupId: number) {
        return await this.get('/portfolio_groups/' + groupId + '/overview', false)
    }

    async getPortfolioFunding(groupId: number) {
        return await this.get('/portfolio_groups/' + groupId + '/funding', false)
    }

    async getPortfolioDistributions(groupId: number) {
        return await this.get('/portfolio_groups/' + groupId + '/distributions', false)
    }

    async getPortfolioConcentration(groupId: number) {
        return await this.get('/portfolio_groups/' + groupId + '/concentration', false)
    }

    async exportPortfolioKpis(groupId: number) {
        return await this.get('/portfolio_groups/' + groupId + '/export', false)
    }

    async exportValuationKpis(scenarioId: number) {
        return await this.get('/operational/valuation/' + scenarioId + '/export', false)
    }

    // Portfolio Staleness
    async getPortfolioStaleness(groupId: number) {
        return await this.get('/portfolio_groups/' + groupId + '/staleness', false)
    }

    async getPortfolioAssetHistory(groupId: number, assetId: number) {
        return await this.get('/portfolio_groups/' + groupId + '/assets/' + assetId + '/history', false)
    }

    async refreshPortfolioAsset(groupId: number, assetId: number) {
        return await this.post('/portfolio_groups/' + groupId + '/refresh_asset/' + assetId, {})
    }

    // Approval Log
    async getPortfolioApprovalLog(groupId: number, limit: number = 50, offset: number = 0) {
        return await this.get('/portfolio_groups/' + groupId + '/approval_log?limit=' + limit + '&offset=' + offset, false)
    }

    async getAssetApprovalLog(groupId: number, assetId: number, limit: number = 50, offset: number = 0) {
        return await this.get('/portfolio_groups/' + groupId + '/assets/' + assetId + '/approval_log?limit=' + limit + '&offset=' + offset, false)
    }

    // Available Companies
    async getAvailableCompanies(modelId: number, scenarioId: number) {
        return await this.get('/portfolio_groups/available_companies?model_id=' + modelId + '&scenario_id=' + scenarioId, false)
    }
}

const apiService = new ApiService(process.env.REACT_APP_API_URL);

export default apiService